import React from "react";
import { Box, Typography } from "@mui/material";

// Import images so bundlers resolve them correctly (fixes “background not displayed”)
// import img1 from "../../../images/clouds3.png"
import img3 from "../../../images/g2010-aspire-national-champions.png";
import img2 from "../../../images/logan-ruggiero-darren.png";


/**
 * This mirrors the original Materialize layout exactly:
 * - Top row: two columns that are 50/50 on large (>=993px), stacked on medium/small (<993px).
 * - Right side contains two rows:
 *   Row A (visible on med & up): "Creating Unforgettable Moments" (left) | photo (right)
 *   Row B (visible on med & up): photo (left) | "Developing Elite Players" (right)
 *   Row C (visible on small only): "Developing Elite Players" (first) then photo (second)
 *
 * We use raw media queries (992/993 & 600/601) to match Materialize’s breakpoints,
 * instead of MUI’s default (600/900/1200), so behavior is identical.
 */
const PicturesAndText2 = () => {
  // Materialize-like breakpoints
  const upTo992 = "@media (max-width: 992px)";
  const from993 = "@media (min-width: 993px)";
  const smallOnly = "@media (max-width: 600px)";
  const medAndUp = "@media (min-width: 601px)";

  // Helpers that replicate col l6 m12 s12 exactly
  const colL6_M12_S12 = {
    [from993]: { flexBasis: "50%", maxWidth: "50%" }, // large: half width
    [upTo992]: { flexBasis: "100%", maxWidth: "100%" }, // med & small: full width
    padding: 0,
  };

  // Helpers that replicate col l6 m6 s12 for inner right-side rows
  const colL6_M6_S12 = {
    [medAndUp]: { flexBasis: "50%", maxWidth: "50%" }, // med & up: half width
    [smallOnly]: { flexBasis: "100%", maxWidth: "100%" }, // small only: full width
    padding: 0,
  };

  const accomplishments = [
    {
      season: "2025-2026",
      text:
        "4 New York State Champions, had 6 teams qualify for USYS Nationals, 2 boys teams qualify for MLS Next Nationals, and 2 girls teams qualify for GA Aspire Nationals, where the Girls 2010 team won the GA Aspire National Championship. The boys 2010 and girls 2011 made it to the USYS National Quarter Finals.",
    },
    {
      season: "2024-2025",
      text:
        "4 New York State Champions and 2 New York State Finalists, qualified 4 teams to USYS Regionals, and finished the year with 1 Regional Finalist and 1 team appearance at USYS Nationals.",
    },
    {
      season: "2023-2024",
      text:
        "5 New York State Champions and 1 Finalist, had 8 teams qualify for USYS Regionals (most in the Eastern Region), topping the year off with 2 Regional Finalists and 2 team appearances at USYS Nationals, where the club had a team make it the final 4 and earn 1 USYS National Semi-Finalist Title.",
    },
    {
      season: "Preceding Years",
      text:
        "Captured numerous State, Regional, and National Titles, while consistently producing #1 ranked teams in the Nation, solidifying ourselves as one of the most successful USYS clubs in the country.",
    },
  ];

  const scrollToFeatureBoxes = () => {
    const target = document.getElementById("about-feature-boxes");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <Box id="pictures-and-text-2" sx={{ width: "100%", bgcolor: "#0a0d14" }}>
      {/* Top-level row (like .row) */}
      <Box sx={{ display: "flex", flexWrap: "wrap", margin: 0 }}>
        {/* Left Column: Big Picture (col l6 m12 s12) */}
        <Box sx={colL6_M12_S12} color="white" height='500px' >
            <Typography variant="h4" color="white" gutterBottom 
                sx={{
                    fontWeight: 600,
                    textAlign: 'center', 
                    mt: '50px',
                }}>
                Recent Titles
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mx: { xs: 2, md: 3 }, mt: 3, mb: 2, maxHeight: "300px", overflowY: "auto", pr: 1 }}>
              {accomplishments.map((item) => (
                <Box
                  key={item.season}
                  sx={{
                    bgcolor: "rgba(13, 71, 161, 0.08)",
                    // bgcolor: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.14)",
                    borderRadius: "12px",
                    p: { xs: 2, md: 2.5 },
                    boxShadow: "0 10px 30px rgba(0,0,0,0.22)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: "red",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      mb: 0.75,
                    }}
                  >
                    {item.season}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "rgba(255,255,255,0.92)",
                      lineHeight: 1.7,
                      fontSize: { xs: "0.95rem", md: "1rem" },
                    }}
                  >
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
              <Box
                component="button"
                type="button"
                onClick={scrollToFeatureBoxes}
                aria-label="Scroll to featured boxes"
                sx={{
                  border: "none",
                  background: "transparent",
                  color: "red",
                  fontSize: "2rem",
                  cursor: "pointer",
                  p: 0,
                  lineHeight: 1,
                  transition: "transform 0.2s ease",
                  "&:hover": { transform: "translateY(3px)" },
                }}
              >
                ↓
              </Box>
            </Box>
        </Box>

        {/* Right Column (col l6 m12 s12) */}
        <Box id="about-feature-boxes" sx={colL6_M12_S12}>
          {/* Row A: visible on med & up (matches .row with no "hide" and m6/l6 columns) */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              margin: 0
            }}
          >
            {/* Text left */}
            <Box sx={colL6_M6_S12}>
              <Box
                id="winning-culture-text"
                sx={{
                  height: 250,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  bgcolor: "rgb(250, 250, 250)",
                  color: "rgb(0, 0, 31)",
                  p: 3,
                  textAlign: "center",
                  fontStyle: "italic",
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 500, m: 0 }}>
                  WINNING CULTURE
                </Typography>
              </Box>
            </Box>

            {/* Photo right */}
            <Box sx={colL6_M6_S12}>
              <Box
                id="winning-culture-pic"
                sx={{
                  backgroundImage: `linear-gradient(120deg, rgba(0, 0, 49, 0.45) 0%, rgba(255, 0, 0, 0.5) 100%), url(${img2})`,
                  backgroundPosition: "center 0px",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  height: 250,
                }}
              />
            </Box>
          </Box>

          {/* Row B: visible on med & up (matches .row.hide-on-small-only) */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              margin: 0,
              [smallOnly]: { display: "none" }, // hide on small only
            }}
          >
            {/* Photo left */}
            <Box sx={colL6_M6_S12}>
              <Box
                id="achieve-as-a-team-pic"
                sx={{
                  backgroundImage: `linear-gradient(rgba(255, 0, 0, 0.2) 0%,rgba(0, 0, 49, 0.30) 50%, rgba(0, 0, 49, 0.30) 80%, rgba(255, 0, 0, 0.35) 100%), url(${img3})`,
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  height: 250,
                }}
              />
            </Box>

            {/* Text right */}
            <Box sx={colL6_M6_S12}>
              <Box
                id="achieve-as-a-team-text"
                sx={{
                  height: 250,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  bgcolor: "rgb(250, 250, 250)",
                  color: "rgb(0, 0, 31)",
                  p: 3,
                  textAlign: "center",
                  fontStyle: "italic",
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 500, m: 0 }}>
                  ACHIEVE AS A TEAM
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Row C: small only version (matches .row.hide-on-med-and-up) */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              margin: 0,
              [medAndUp]: { display: "none" }, // hide on med & up
            }}
          >
            {/* Text first */}
            <Box sx={colL6_M6_S12}>
              <Box
                id="achieve-as-a-team-text"
                sx={{
                  height: 250,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  bgcolor: "rgb(250, 250, 250)",
                  color: "rgb(0, 0, 31)",
                  p: 3,
                  textAlign: "center",
                  fontStyle: "italic",
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 500, m: 0 }}>
                  ACHIEVE AS A TEAM
                </Typography>
              </Box>
            </Box>

            {/* Photo second */}
            <Box sx={colL6_M6_S12}>
              <Box
                id="achieve-as-a-team-pic"
                sx={{
                  backgroundImage: `linear-gradient(rgba(255, 0, 0, 0.2) 0%,rgba(0, 0, 49, 0.30) 50%, rgba(0, 0, 49, 0.30) 80%, rgba(255, 0, 0, 0.35) 100%), url(${img3})`,
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  height: 250,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PicturesAndText2;


